import { createRouter, createWebHistory, RouteRecordRaw, RouterView } from 'vue-router'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'
import { authStore } from '../stores/auth'
import { usePlanStore } from '../stores/plan'
import { permissionStore } from '../stores/permissions'
import { getRoutePlanGate } from '../services/plan/routeGates'

const heimdall = async (param: string): Promise<boolean> => {
  const perm = permissionStore()

  // Ensure permissions are available before checking (important after reload / first navigation)
  if (!perm.permissions || perm.permissions.length === 0) {
    perm.loadPermissionsFromStorage()
  }
  if (!perm.permissions || perm.permissions.length === 0) {
    await perm.loadMyPermissions()
  }

  return perm.hasPermission(param)
}

const routeAllowedByPlan = (routeName: unknown, planStore: ReturnType<typeof usePlanStore>): boolean => {
  const gate = getRoutePlanGate(routeName)
  if (!gate) return true

  if (gate.module && !planStore.hasModule(gate.module)) {
    return false
  }

  if (gate.feature && !planStore.hasFeature(gate.feature)) {
    return false
  }

  return true
}

const routes: Array<RouteRecordRaw> = [
  {
    // Instagram OAuth callback must be reachable directly by the provider.
    // Keep this route public and before the catch-all redirect.
    name: 'instagram-callback',
    path: '/instagram/callback',
    component: () => import('../pages/instagram/InstagramCallback.vue'),
  },
  {
    name: 'select-company',
    path: '/select-company',
    component: () => import('../pages/auth/CompanySelection.vue'),
  },

  // Public reset-password route (some email links point to `/reset-password/:token` without `/auth`)
  {
    name: 'reset-password-root',
    path: '/reset-password/:token?',
    component: () => import('../pages/auth/ResetPassword.vue'),
  },

  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'login' },
  },
  {
    name: 'admin',
    path: '/', // /admin
    component: AppLayout,
    redirect: { name: 'dashboard' },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'onboarding-setup',
        path: 'onboarding/setup',
        component: () => import('../pages/onboarding/OnboardingSetup.vue'),
        meta: { requiresAdmin: true },
      },
      {
        name: 'crm',
        path: 'crm',
        component: () => import('../pages/crm/Chat.vue'),
      },
      {
        name: 'agenda',
        path: 'agenda/:modo?', // 'mes' | 'semana' | 'dia'
        component: () => import('../pages/agenda/AgendaPage.vue'),
        props: true,
      },
      {
        name: 'tarefas',
        path: 'tarefas',
        component: () => import('../pages/tarefa/TarefasPage.vue'),
        meta: { title: 'Tarefas' },
      },
      {
        name: 'oportunidades',
        path: 'oportunidades',
        redirect: { name: 'opportune' },
        children: [
          {
            name: 'opportune',
            path: '',
            component: () => import('../pages/oportunidades/Oportunidades.vue'),
          },
          {
            name: 'crm-history',
            path: 'history',
            component: () => import('../pages/contato/Historico.vue'),
          },
          {
            name: 'lead-list',
            path: 'leadlist',
            component: () => import('../pages/crm/LeadTable.vue'),
          },
        ],
      },
      {
        name: 'demos',
        path: 'demo',
        children: [
          {
            name: 'lead-contact-demo',
            path: 'lead-contact',
            component: () => import('../pages/demo/LeadContactDemo.vue'),
            meta: {
              title: 'Demonstração: Busca de Leads e Contatos',
              permission: 'ver demo leads',
            },
          },
          {
            name: 'lead-test',
            path: 'lead-test',
            component: () => import('../pages/demo/LeadTest.vue'),
            meta: {
              title: 'Demonstração: Teste de Leads',
              permission: 'ver demo testes',
            },
          },
        ],
      },
      // {
      //   name: 'lead-contact-demo',
      //   path: 'demo/lead-contact',
      //   component: () => import('../pages/demo/LeadContactDemo.vue'),
      //   meta: {
      //     title: 'Demonstração: Busca de Leads e Contatos',
      //     // ✅ SEGURO: Usa sistema de permissões do backend
      //     // 🔧 CONFIGURAÇÃO: Admin define no painel administrativo quem tem esta permissão
      //     permission: 'ver demo leads',
      //   },
      // },
      // {
      //   name: 'lead-test',
      //   path: 'demo/lead-test',
      //   component: () => import('../pages/demo/LeadTest.vue'),
      //   meta: {
      //     title: 'Demonstração LeadTest',
      //     // ✅ SEGURO: Usa sistema de permissões do backend
      //     // 🔧 CONFIGURAÇÃO: Admin define no painel administrativo quem tem esta permissão
      //     permission: 'ver demo testes',
      //   },
      // },
      {
        name: 'businessHourLegacy',
        path: 'businessHour',
        redirect: { name: 'e_business_hour_legacy' },
        children: [
          {
            name: 'e_business_hour_legacy',
            path: '',
            redirect: { name: 'bh_list' },
          },
          {
            name: 'e_business_hour_create_legacy',
            path: 'create',
            redirect: { name: 'bh_create' },
          },
        ],
      },
      {
        name: 'templateLegacy',
        path: 'template',
        component: RouterView,
        redirect: { name: 'e_template_legacy' },
        children: [
          {
            name: 'e_template_legacy',
            path: '',
            redirect: { name: 'temp_list' },
          },
          {
            name: 'e_template_create_legacy',
            path: 'create',
            redirect: { name: 'temp_create' },
          },
          {
            name: 'e_template_edit_legacy',
            path: 'edit/:slug',
            redirect: (to) => ({ name: 'temp_edit', params: { slug: to.params.slug } }),
          },
        ],
      },
      {
        name: 'reports',
        path: 'reports',
        component: RouterView,
        redirect: { name: 'reports-templates' },
        children: [
          {
            name: 'reports-general',
            path: 'general',
            component: () => import('../pages/reports/GeneralReportsList.vue'),
            meta: { title: 'Relatórios Gerais', permission: 'reports:access' },
          },
          {
            name: 'reports-templates',
            path: '',
            component: () => import('../pages/reports/ReportTemplatesList.vue'),
            meta: { title: 'Relatórios - Templates', permission: 'reports:access' },
          },
          {
            name: 'reports-template-edit',
            path: 'templates/:id',
            component: () => import('../pages/reports/ReportTemplateEditor.vue'),
            meta: { title: 'Relatórios - Template', permission: 'reports:access' },
          },
          {
            name: 'reports-executions',
            path: 'executions',
            component: () => import('../pages/reports/ReportExecutionsList.vue'),
            meta: { title: 'Relatórios - Execuções', permission: 'reports:access' },
          },
        ],
      },
      {
        name: 'painel',
        path: 'painel',
        component: () => import('../pages/crm/Kanban.vue'),
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
      },
      {
        name: 'usuarioLogado',
        path: 'me',
        redirect: { name: 'me_list' },
        children: [
          {
            name: 'me_list',
            path: '',
            component: () => import('../pages/usuarios/UsuarioLogado.vue'),
          },
          {
            name: 'me_edit',
            path: 'edit/:slug',
            component: () => import('../pages/usuarios/UsuarioEdit.vue'),
          },
        ],
      },
      {
        name: 'cliente',
        path: 'cliente',
        redirect: { name: 'c_list' },
        children: [
          {
            name: 'c_list',
            path: '',
            component: () => import('../pages/clientes/ClientesTable.vue'),
            meta: { permission: 'view cliente' },
          },
          {
            name: 'c_create',
            path: 'create',
            component: () => import('../pages/clientes/ClientesCreate.vue'),
            meta: { permission: 'add cliente' },
          },
          {
            name: 'c_edit',
            path: 'edit/:slug',
            component: () => import('../pages/clientes/ClienteEdit.vue'),
            meta: { permission: 'change cliente' },
          },
          {
            name: 'c_doc',
            path: 'docs/:slug',
            component: () => import('../pages/clientes/ClienteDoc.vue'),
            meta: { permission: 'add documento cliente' },
          },
        ],
      },
      {
        name: 'veiculo',
        path: 'veiculo',
        children: [
          {
            name: 'v_list',
            path: '',
            component: () => import('../pages/veiculos/VeiculosTable.vue'),
            meta: { permission: 'view veículo' },
          },
          {
            name: 'v_sold',
            path: 'vendidos',
            component: () => import('../pages/veiculos/VeiculosTable.vue'),
            meta: { permission: 'view veículo' },
          },
          {
            name: 'v_create',
            path: 'create',
            component: () => import('../pages/veiculos/VeiculosCreate.vue'),
            meta: { permission: 'add veículo' },
          },
          {
            name: 'v_edit',
            path: 'edit/:slug',
            component: () => import('../pages/veiculos/VeiculosEdit.vue'),
            meta: { permission: 'change veículo' },
          },
          /* {
            name: 'baixados',
            path: 'baixados',
            component: () => import('../pages/payments/PaymentsPage.vue'),
            meta: { permission: 'view veículo' },
          }, */
          {
            name: 'v_doc',
            path: 'docs/:slug',
            component: () => import('../pages/veiculos/VeiculosDoc.vue'),
            meta: { permission: 'view documento veículo' },
          },
          {
            name: 'v_pho',
            path: 'fotos/:slug',
            component: () => import('../pages/veiculos/VeiculosPhotos.vue'),
            meta: { permission: 'view foto veículo' },
          },
        ],
      },
      {
        name: 'empresa',
        path: 'empresa',
        children: [
          {
            name: 'e_list',
            path: '',
            component: () => import('../pages/empresa/EmpresaPage.vue'),
            meta: { permission: 'view empresa' },
          },
          {
            name: 'e_edit',
            path: 'edit/:slug',
            component: () => import('../pages/empresa/EmpresaEdit.vue'),
            meta: { permission: 'change empresa' },
          },
          {
            name: 'e_fil',
            path: 'files/:slug',
            component: () => import('../pages/empresa/EmpresaFiles.vue'),
            meta: { permission: 'change empresa' },
          },
          {
            name: 'e_config',
            path: 'config',
            component: () => import('../pages/empresa/EmpresaConfigDetails.vue'),
            meta: { permission: 'view empresa' },
          },
          {
            name: 'e_bot',
            path: 'bot',
            component: () => import('../pages/empresa/bot/BotControlCenterPage.vue'),
            meta: { permission: 'view_bot_configuration' },
          },
          {
            name: 'e_config_edit',
            path: 'config/edit',
            component: () => import('../pages/empresa/EmpresaConfigForm.vue'),
            meta: { permission: 'view empresa' },
          },
          {
            name: 'e_storage',
            path: 'storage',
            component: () => import('../pages/empresa/EmpresaStorage.vue'),
            meta: { permission: 'view empresa' },
          },
          {
            name: 'e_template',
            path: 'template',
            component: RouterView,
            redirect: { name: 'temp_list' },
            children: [
              {
                name: 'temp_list',
                path: '',
                component: () => import('../pages/templates/TemplateTable.vue'),
              },
              {
                name: 'temp_create',
                path: 'create',
                component: () => import('../pages/templates/TemplateCreate.vue'),
              },
              {
                name: 'temp_edit',
                path: 'edit/:slug',
                component: () => import('../pages/templates/TemplateEdit.vue'),
              },
            ],
          },
          {
            name: 'e_business_hour',
            path: 'businessHour',
            redirect: { name: 'bh_list' },
            meta: { permission: 'view empresa' },
            children: [
              {
                name: 'bh_list',
                path: '',
                component: () => import('../pages/business/BusinessHourTable.vue'),
                meta: { permission: 'view empresa' },
              },
              {
                name: 'bh_create',
                path: 'create',
                component: () => import('../pages/business/BusinessHourCreate.vue'),
                meta: { permission: 'change businesshour' },
              },
            ],
          },
        ],
      },
      {
        name: 'venda',
        path: 'venda',
        children: [
          {
            name: 've_list',
            path: '',
            component: () => import('../pages/vendas/VendaTable.vue'),
            meta: { permission: 'view venda' },
          },
          {
            name: 've_create',
            path: 'create',
            component: () => import('../pages/vendas/VendaCreate.vue'),
            meta: { permission: 'add venda' },
          },
          {
            name: 've_edit',
            path: 'edit/:slug',
            component: () => import('../pages/vendas/VendaEdit.vue'),
            meta: { permission: 'change venda' },
          },
        ],
      },
      {
        name: 'usuario',
        path: 'usuario',
        children: [
          {
            name: 'u_list',
            path: '',
            component: () => import('../pages/usuarios/UsuarioTable.vue'),
            meta: { permission: 'view usuario' },
          },
          {
            name: 'u_create',
            path: 'create',
            component: () => import('../pages/usuarios/UsuarioCreate.vue'),
            meta: { permission: 'add usuario' },
          },
          {
            name: 'u_edit',
            path: 'edit/:slug',
            component: () => import('../pages/usuarios/UsuarioEdit.vue'),
            meta: { permission: 'change usuario' },
          },
          {
            name: 'u_perm',
            path: 'permissions/:slug',
            component: () => import('../pages/usuarios/UsuarioPermission.vue'),
            meta: { permission: 'can_give_permissions' },
          },
        ],
      },
      {
        name: 'relatorio',
        path: 'relatorio',
        children: [
          {
            name: 'rel_procuracao',
            path: 'procuracao',
            redirect: { name: 'reports-templates' },
          },
        ],
      },
      {
        name: 'procurador',
        path: 'procurador',
        children: [
          {
            name: 'proc_list',
            path: '',
            component: () => import('../pages/clientes/ProcuradorTable.vue'),
            meta: { permission: 'view empresa' },
          },
          {
            name: 'proc_create',
            path: 'create',
            component: () => import('../pages/clientes/ProcuradorCreate.vue'),
            meta: { permission: 'view empresa' },
          },
          {
            name: 'proc_edit',
            path: 'edit/:slug',
            component: () => import('../pages/clientes/ProcuradorEdit.vue'),
            meta: { permission: 'view empresa' },
          },
        ],
      },
      {
        name: 'negociacao',
        path: 'negociacao',
        redirect: { name: 'propostas-list' },
        meta: {
          breadcrumb: 'Negociações',
        },
        children: [
          {
            name: 'propostas-list',
            path: '',
            component: () => import('../pages/negociacao/PropostaList.vue'),
            meta: {
              breadcrumb: 'Propostas',
              icon: 'handshake',
              title: 'Negociações - Propostas',
            },
          },
          {
            name: 'proposta-create',
            path: 'novo',
            component: () => import('../pages/negociacao/PropostaForm.vue'),
            meta: {
              breadcrumb: 'Nova Proposta',
              parent: 'propostas-list',
              title: 'Nova Proposta',
            },
          },
          {
            name: 'proposta-detail',
            path: ':slug',
            component: () => import('../pages/negociacao/PropostaDetail.vue'),
            meta: {
              breadcrumb: 'Detalhes',
              parent: 'propostas-list',
              title: 'Detalhes da Proposta',
            },
          },
          {
            name: 'proposta-edit',
            path: ':slug/editar',
            component: () => import('../pages/negociacao/PropostaForm.vue'),
            meta: {
              breadcrumb: 'Editar',
              parent: 'propostas-list',
              title: 'Editar Proposta',
            },
          },
          {
            name: 'proposta-itens',
            path: ':slug/itens',
            component: () => import('../pages/negociacao/PropostaItens.vue'),
            meta: {
              breadcrumb: 'Itens',
              parent: 'propostas-list',
              title: 'Veículos da Proposta',
            },
          },
          {
            name: 'propostas-kanban',
            path: 'kanban',
            component: () => import('../pages/negociacao/PropostaKanban2.vue'),
            meta: {
              breadcrumb: 'Kanban',
              parent: 'propostas-list',
              icon: 'view_kanban',
              title: 'Kanban de Propostas',
            },
          },
          {
            name: 'propostas-dashboard',
            path: 'dashboard',
            component: () => import('../pages/negociacao/PropostaDashboard.vue'),
            meta: {
              breadcrumb: 'Dashboard',
              parent: 'propostas-list',
              icon: 'dashboard',
              title: 'Dashboard de Negociações',
            },
          },
        ],
      },
      {
        name: 'contato',
        path: 'contato',
        redirect: { name: 'cont_list' },
        children: [
          {
            name: 'cont_list',
            path: '',
            component: () => import('../pages/contato/ContatoTable.vue'),
          },
          {
            name: 'cont_create',
            path: 'create',
            component: () => import('../pages/contato/ContatoCreate.vue'),
          },
          {
            name: 'cont_edit',
            path: 'edit/:slug',
            component: () => import('../pages/contato/ContatoEdit.vue'),
          },
          {
            name: 'tags-list',
            path: 'tags',
            component: () => import('../pages/tags/TagsTable.vue'),
          },
          {
            name: 'tags-create',
            path: 'tags/create',
            component: () => import('../pages/tags/TagsForm.vue'),
            meta: { requiresAdmin: true },
          },
          {
            name: 'tags-edit',
            path: 'tags/edit/:id',
            component: () => import('../pages/tags/TagsForm.vue'),
            meta: { requiresAdmin: true },
          },
        ],
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        name: 'reset-password',
        path: 'reset-password/:token?',
        component: () => import('../pages/auth/ResetPassword.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'onboarding-signup',
        path: 'onboarding/:token',
        component: () => import('../pages/auth/OnboardingSignup.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
  {
    name: '403',
    path: '/forbidden',
    component: () => import('../pages/Forbidden.vue'),
  },
  {
    name: 'verified',
    path: '/verify/:token',
    component: () => import('../pages/Verified.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

router.beforeEach(async (to, from, next) => {
  const auth = authStore()
  const planStore = usePlanStore()
  const isAuthRoute = to.path.startsWith('/auth') || to.name === 'login' || to.name === 'instagram-callback'

  // 1. Check if logged in
  if (!auth.token && !isAuthRoute) {
    return next({ name: 'login' })
  }

  // 2. Check company context for admin routes
  const isAdminRoute = to.matched.some((record) => record.components?.default === AppLayout)
  if (auth.token && isAdminRoute && !auth.cmpId && to.name !== 'select-company') {
    return next({ name: 'select-company' })
  }

  if (auth.token && isAdminRoute && auth.cmpId) {
    try {
      await planStore.ensureLoaded()
    } catch (error) {
      console.error('Erro ao carregar capacidades do plano:', error)
      return next('/forbidden')
    }

    const setupStatus = planStore.summary?.setup?.status
    const requiresSetup = String(auth.empEnabled) !== 'true' || setupStatus === 'setup_required'
    const setupAllowlist = new Set([
      'onboarding-setup',
      'select-company',
      'login',
      '403',
      '404',
      'e_list',
      'e_edit',
      'e_config',
      'e_config_edit',
      'bh_list',
      'bh_create',
    ])
    if (requiresSetup && !setupAllowlist.has(String(to.name))) {
      return next({ name: 'onboarding-setup' })
    }

    const blockedByPlan = to.matched.some((record) => !routeAllowedByPlan(record.name, planStore))
    if (blockedByPlan) {
      console.warn(`Acesso negado por plano para rota '${String(to.name)}'`)
      return next('/forbidden')
    }
  }

  if (to.name === 'u_create' && localStorage.getItem('addU') !== 'true') {
    return next('/forbidden')
  }

  // ✅ Verificação de acesso apenas para administradores
  const requiresAdmin = (to.meta as any).requiresAdmin
  if (requiresAdmin) {
    const isAdmin = String(auth.cmpUrole).toLowerCase() === 'admin'
    if (!isAdmin) {
      console.warn(`Acesso negado: rota '${String(to.name)}' requer administrador`)
      return next('/forbidden')
    }
  }

  // ✅ Verificação de permissões (sistema existente melhorado)
  const requiredPermission = (to.meta as any).permission as string | undefined
  if (requiredPermission) {
    try {
      const hasPermission = await heimdall(requiredPermission)
      if (!hasPermission) {
        console.warn(`Acesso negado: permissão '${requiredPermission}' necessária`)
        return next('/forbidden')
      }
    } catch (error) {
      console.error('Erro ao verificar permissões:', error)
      return next('/forbidden')
    }
  }

  return next()
})

export default router
